import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessagesSquare, Plus, Heart, Send, MessageCircle, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CATEGORIES = ["All", "General", "Health", "Behaviour", "Training", "Nutrition", "Pack Life"];
const timeAgo = (d) => {
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Community() {
  const { user } = useAuth();
  const member = user?.name || user?.email || "You";
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "General" });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get(`/forum/posts?category=${cat}`).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, [cat]); // eslint-disable-line

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Add a title and message"); return; }
    await api.post("/forum/posts", { ...form });
    setForm({ title: "", body: "", category: "General" }); setOpen(false); toast.success("Posted!"); load();
  };
  const like = async (id) => { await api.post(`/forum/posts/${id}/like`); load(); if (active?.id === id) setActive({ ...active, likes: active.likes + 1 }); };
  const sendReply = async () => {
    if (!reply.trim()) return;
    const r = await api.post(`/forum/posts/${active.id}/replies`, { body: reply });
    setReply(""); setActive(r.data); load();
  };

  return (
    <div data-testid="community-page">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-4xl font-extrabold tracking-tight">Community Forum</h1>
          <p className="text-muted-foreground mt-2">Posting as <b>{member}</b> · discuss, ask questions, share advice.</p>
        </div>
        <Button data-testid="open-new-post" onClick={() => setOpen(true)} className="rounded-full"><Plus className="h-4 w-4 mr-1" /> New post</Button>
      </div>

      <div className="flex gap-2 overflow-x-auto scroll-thin pb-2 mb-5">
        {CATEGORIES.map((c) => (
          <button key={c} data-testid={`cat-${c}`} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{c}</button>
        ))}
      </div>

      {posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-14 text-center bg-card/50">
          <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No posts here yet. Be the first to start a discussion!</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {posts.map((p) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`post-${p.id}`} className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs bg-accent/20 text-accent-foreground px-2 py-0.5 rounded-full">{p.category}</span>
                <span className="text-xs text-muted-foreground">{p.author} · {timeAgo(p.date)}</span>
              </div>
              <h3 className="font-head font-semibold text-lg cursor-pointer hover:text-primary" onClick={() => setActive(p)}>{p.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.body}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                <button data-testid={`like-${p.id}`} onClick={() => like(p.id)} className="flex items-center gap-1 hover:text-secondary"><Heart className="h-4 w-4" /> {p.likes || 0}</button>
                <button data-testid={`open-${p.id}`} onClick={() => setActive(p)} className="flex items-center gap-1 hover:text-primary"><MessageCircle className="h-4 w-4" /> {p.replies?.length || 0} replies</button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* New post dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle className="font-head">Start a discussion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's on your mind?" /></div>
            <div><Label>Category</Label>
              <select data-testid="post-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm">
                {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Message</Label><Textarea data-testid="post-body" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Describe what you're experiencing or ask your question..." /></div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-post" onClick={submit} className="rounded-full">Post</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thread dialog */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto scroll-thin">
          {active && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs bg-accent/20 text-accent-foreground px-2 py-0.5 rounded-full">{active.category}</span>
                <span className="text-xs text-muted-foreground">{active.author} · {timeAgo(active.date)}</span>
              </div>
              <h2 className="font-head text-2xl font-bold tracking-tight">{active.title}</h2>
              <p className="text-foreground/90 mt-2 whitespace-pre-wrap">{active.body}</p>
              <button data-testid="thread-like" onClick={() => like(active.id)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-secondary mt-3"><Heart className="h-4 w-4" /> {active.likes || 0}</button>

              <div className="border-t border-border my-4" />
              <h4 className="font-head font-semibold mb-3">{active.replies?.length || 0} replies</h4>
              <div className="space-y-3 mb-4">
                {(active.replies || []).map((r) => (
                  <div key={r.id} className="rounded-xl bg-muted/50 p-3">
                    <div className="text-xs text-muted-foreground mb-1">{r.author} · {timeAgo(r.date)}</div>
                    <p className="text-sm whitespace-pre-wrap">{r.body}</p>
                  </div>
                ))}
                {(!active.replies || active.replies.length === 0) && <p className="text-sm text-muted-foreground">No replies yet — share your advice or experience.</p>}
              </div>
              <div className="flex gap-2 sticky bottom-0 bg-card pt-2">
                <Textarea data-testid="reply-input" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply..." />
                <Button data-testid="send-reply" onClick={sendReply} className="rounded-full self-end"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
